import type Device from "./Device";
import KnxBridge, { MemoryStats } from "../KnxBridge";
import BaseObject from "./BaseObject";
import KnxProductData from "../KnxProductdata";
import { itx } from "../translationItx/itx";
import { downloadMode } from "../installation/DeviceProgramminCard";

export enum DeviceStatus {
  noNetworkConnection = "0", // e.g. Gateway not connected / Network not online
  loading = "1", // show spinner in UI

  notAvailable = "2", // Connection on; PhyAddr not found in N/W, (verify false; addrExist false; progMode false)
  available = "3", // Connection on; PhyAddr found, (addrExist true; verify true; progMode false)
  appProgramNotInstalled = "4", // Connection on; PhyAddr found, (addrExist true; verify false; progMode false)  appVersionFromMem = "0000000000"
  deviceMismatch = "5", // Connection on; PhyAddr found, (addrExist true; verify false; progMode false)
  availableInProgMode = "6", // Connection on; PhyAddr found, (addrExist true; verify true; progMode true)

  downloading = "7",
  unloading = "8",

  loadError = "9", // In case of internal code error
  noKNXLinkEstablished = "10", // Gateway running but IP link disconnected
  notSupported = "11",
}

export default class DeviceProgrammingInfo extends BaseObject {
  device: Device;
  deviceInProgMode: string = "";
  productData: KnxProductData | undefined;
  ready: boolean = false;

  initialLoad = true;
  loadOnline = false;
  deviceStatus: DeviceStatus = DeviceStatus.noNetworkConnection;
  deviceReachable: boolean = true;
  programmedOnce: boolean = false;

  // programMode: boolean | undefined;
  // addressExist: deviceStatus = deviceStatus.noNetworkConnection;
  // verifiedDevice: boolean | undefined;
  // unloadInProgress: boolean = false;
  // downloadInProgress: boolean = false;

  aPVersion = "";
  maskFromAP = "";
  manIdFromAP = "";
  orderIdFromAP = "";
  aPNumberFromAP = "";
  serialNrFromMem = "";
  manufacturerName = "";
  aPVersionSubversion = "";
  NetworkErrorText = itx("knxBridge.netError", "Network Error");

  memoryStats: MemoryStats = {
    testedOnce: false,
    address: "",
    fullName: "",
    apVersion: "",
    maskVersion: "",
    manufacturer: "",
    addrMemDiff: 0,
    assocMemDiff: 0,
    comObjMemDiff: 0,
    paramMemDiff: 0,
    totalMemDiff: 0,
    failureCount: 0,
  };

  selectedUI = false;

  constructor(device: Device) {
    super();
    this.device = device;
    this.productData = device.template?.knxProductData;
    if (this.productData) {
      this.maskFromAP = this.productData.productInfo.MaskVersion
        ? this.productData.productInfo.MaskVersion.split("-")[1]
        : "";
      this.manIdFromAP = this.productData.productInfo.ManufacturerId
        ? this.productData.productInfo.ManufacturerId.split("-")[1]
        : "";
      this.orderIdFromAP = this.productData.productInfo.OrderNumber
        ? this.productData.productInfo.OrderNumber.split("-")[1]
        : "";
      this.aPNumberFromAP = this.productData.productInfo.ApplicationNumber
        ? this.productData.productInfo.ApplicationNumber
        : "";
      this.aPVersion = this.productData.productInfo.ApplicationVersion
        ? this.productData.productInfo.ApplicationVersion
        : "";
      this.aPVersionSubversion = this.aPVersion
        ? `v${Math.floor(parseInt(this.aPVersion) / 16)}.${parseInt(this.aPVersion) % 16}`
        : "-";

      this.memoryStats.fullName = `${device.fullTag} ${device.fullname}`;
      this.memoryStats.address = device.fullPhysicalAddress;
      this.memoryStats.manufacturer = `${this.manufacturerName} (${this.manIdFromAP})`;
      this.memoryStats.apVersion = `${this.aPVersion} (${this.aPVersionSubversion})`;
      this.memoryStats.maskVersion = this.maskFromAP;
    }
  }

  async verifyDevice(progMode?: boolean) {
    // this.verifiedDevice = false;
    this.productData = this.device.template?.knxProductData;
    if (this.productData) {
      let isIPRouterMask = false;

      // Verify Mask Version
      let validMask = true;
      const maskFromMem = await KnxBridge.getDeviceProperty(this.device.fullPhysicalAddress, "83", "0"); // PID 83
      if (this.maskFromAP.toLowerCase() == "091a" || maskFromMem.toLowerCase() == "091a") {
        isIPRouterMask = true;
      }

      // Some devices like BuschJeager have no mask version as an accesible property
      // So for such devices we assume by default that it is valid.
      // if (maskFromMem == "") validMask = true;
      // else validMask = this.maskFromAP.toLowerCase() == maskFromMem.toLowerCase();

      // Old Code (The folowing commented section is now handled under Application Version)
      // Verify Manufacturer Id
      //   const manIdFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "12", "0"); // PID 12
      //   const validManufacturer = this.manIdFromAP == manIdFromMem;
      // Verify ApplicationNumber / HardwareType
      //   const APNumberFromAPInt = parseInt(this.aPNumberFromAP);
      //   const APNumberFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "78", "0"); // PID 78
      //   const APNumberFromMemInt = parseInt(APNumberFromMem, 16);
      //   const validAPNumber = APNumberFromAPInt == APNumberFromMemInt;

      // Application Version (Has 3 information:  Manufacturer, Application Number and Version)
      // E.g. 0083 013b 32 : Manufacturer MDT (0083), App Number 0x013b = 315, Version 0x32 = 50 = 3.2
      let validAppVersion = false;
      let appVersionFromMem: string = "";
      appVersionFromMem = await KnxBridge.getDeviceProperty(this.device.fullPhysicalAddress, "13", "3"); // ObjType 3, PID 13
      if (appVersionFromMem == "") {
        appVersionFromMem = await KnxBridge.getDeviceProperty(this.device.fullPhysicalAddress, "13", "4"); // ObjType 4, PID 13
      }

      // 15.01.2025 : Handle scenario where applicationProgram not installed on device.
      // 12.01.2025 : IP Interface devices such as SCN-IP000.03 do not have accessible PID 13 for Application Object 4
      // For such devices we ignore such check
      if (appVersionFromMem == "0000000000") {
        // Second condition implies application program is not installed on physical device
        this.deviceStatus = DeviceStatus.appProgramNotInstalled;
        // this.verifiedDevice = undefined;
        return this.deviceStatus;
      }

      let isDeviceIPInterface: boolean = false;
      if (appVersionFromMem == "") {
        // Verify that device is of type IP Router / IP Interface
        const deviceObjects = await KnxBridge.getDeviceObjectTypes(this.device);
        if (deviceObjects.Router != undefined || deviceObjects.KNXnetIPParameter != undefined)
          isDeviceIPInterface = true;
        if (isDeviceIPInterface) validAppVersion = true;
      }

      if (!isDeviceIPInterface) {
        const manIdAltFromAP = this.productData.productInfo.OriginalManufacturer
          ? this.productData.productInfo.OriginalManufacturer.split("-")[1]
          : "";
        if (appVersionFromMem && appVersionFromMem.length == 10) {
          const manufacturer = appVersionFromMem.substring(0, 4);
          const appNumber = appVersionFromMem.substring(4, 8);
          const version = appVersionFromMem.substring(8, 10);
          if (
            manufacturer.toLowerCase() == this.manIdFromAP.toLowerCase() ||
            manufacturer.toLowerCase() == manIdAltFromAP.toLowerCase()
            // &&
            // (Math.abs(APNumberFromAPInt - parseInt(appNumber, 16)) < 2 ||
            //   parseInt(this.aPVersion) == parseInt(version, 16))
          ) {
            validAppVersion = true;
          } else {
            this.deviceStatus = DeviceStatus.deviceMismatch;
          }
        }
      }

      // Verify Order Info (for now this is checked only for MDT Devices)
      let validOrderId = true; // Set to true for all manufacturers except MDT for code consistency
      // if (this.manIdFromAP == "0083" && this.orderIdFromAP != "KNX USB Interface") {
      //   validOrderId = false;
      //   const orderFromMem = await KnxBridge.getDeviceProperty(device.fullPhysicalAddress, "15", "0"); // PID 15
      //   const name = KnxBridge.hexToAscii(orderFromMem.substring(2, 14));
      //   const vers = orderFromMem.substring(14, 16);

      //   const oid = this.orderIdFromAP.split(".");
      //   const nameRegex = new RegExp(oid[0].replace("x", "."));
      //   const nameIsEqual = nameRegex.test(name);

      //   if (oid.length == 2 && nameIsEqual && vers == oid[1]) validOrderId = true;
      // }

      if (validAppVersion && validMask && validOrderId) {
        this.deviceStatus = progMode ? DeviceStatus.availableInProgMode : DeviceStatus.available;
      } else if (!validAppVersion && this.deviceStatus == DeviceStatus.loading) {
        this.deviceStatus = DeviceStatus.appProgramNotInstalled;
      } else {
        this.deviceStatus = DeviceStatus.notAvailable;
      }
    }
    return this.deviceStatus;
  }

  async setDeviceStatus() {
    this.serialNrFromMem = this.serialNrFromMem
      ? this.serialNrFromMem
      : await KnxBridge.getDeviceProperty(this.device.fullPhysicalAddress, "11", "0");
    // const mode = await KnxBridge.isDeviceInProgramMode(this.device.fullPhysicalAddress);
    const mode = KnxBridge.deviceInProgMode == this.device.fullPhysicalAddress ? "success" : "";
    await this.verifyDevice(mode == "success");
  }

  async loadOnlineInfos(bridgeConnected: boolean) {
    this.loadOnline = false;
    this.initialLoad = false;
    try {
      if (!bridgeConnected) {
        this.deviceStatus = DeviceStatus.noNetworkConnection;
      } else if (this.productData) {
        this.deviceStatus = DeviceStatus.loading;
        let res;
        try {
          res = await KnxBridge.isDevicePresentInNetwork(this.device.fullPhysicalAddress);
          this.deviceReachable = true;
        } catch (error: any) {
          if (error.toString().includes("deviceNotReachable")) {
            this.deviceReachable = false;
          }
        }

        if (res == "success") {
          await this.setDeviceStatus();
        } else if (res == undefined || res == "Network Error" || res.toLowerCase().includes("not found") || res == "") {
          this.deviceStatus = DeviceStatus.notAvailable;
        } else {
          this.deviceStatus = DeviceStatus.notAvailable; // Placeholder
        }
      } else {
        this.deviceStatus = DeviceStatus.notAvailable; // Placeholder ProductData not available
      }
    } finally {
      this.updateUI();
    }
  }

  setDeviceInProgMode(deviceAddress: string) {
    this.deviceInProgMode = deviceAddress;
    const progMode = deviceAddress == this.device.fullPhysicalAddress;
    this.deviceStatus = progMode ? DeviceStatus.availableInProgMode : this.deviceStatus;
    this.updateUI();
  }

  updateDeviceStatus(deviceStatus: DeviceStatus) {
    this.deviceStatus = deviceStatus;
    this.updateUI();
  }

  updateDeviceSelection(value?: boolean) {
    if (value) this.selectedUI = value;
    else this.selectedUI = !this.selectedUI;
    this.updateUI();
  }

  updateDeviceMemoryStats(memoryStats: MemoryStats) {
    this.memoryStats = memoryStats;
    this.memoryStats.testedOnce = true;
  }

  updateUI() {
    this.raiseOnChange("onlineInfos");
  }

  updateProgramStat(mode: number, unload: boolean) {
    if (
      (mode == downloadMode.parameters ||
        mode == downloadMode.parametersDownloadAndVerify ||
        mode == downloadMode.partialDownload ||
        mode == downloadMode.downloadApplication ||
        mode == downloadMode.downloadIndividualAddress ||
        mode == downloadMode.overwriteIndividualAddress ||
        unload) &&
      !this.programmedOnce
    ) {
      this.programmedOnce = true;
      state.programStat.numberOfProgrammedDevices++;
    }
    if (
      mode == downloadMode.parameters ||
      mode == downloadMode.downloadApplication ||
      mode == downloadMode.downloadIndividualAddress ||
      mode == downloadMode.parametersDownloadAndVerify ||
      mode == downloadMode.partialDownload ||
      mode == downloadMode.overwriteIndividualAddress ||
      unload
    )
      state.programStat.numberOfProgrammedProcesses++;
  }

  public get className(): string {
    return "DeviceProgrammingInfo"; // no itx
  }
}
